import {
  AFTN_SAFE_REGEX
} from "./chunk-2USE66YR.mjs";

// src/common/field-schemas.ts
import { z } from "zod";
var fieldSchemas = {
  /**
   * Country slug (lowercase letters and hyphens)
   * @example "poland", "united-states"
   */
  countrySlug: z.string().min(2).toLowerCase().regex(
    /^[a-z-]+$/,
    "Country slug must contain only lowercase letters and hyphens"
  ),
  /**
   * IATA country/airport code (3 uppercase letters)
   * @example "USA", "POL", "GBR" (country) or "WAW", "JFK" (airport)
   * @warning Do NOT use this for AIRLINE IATA codes — airlines use 2-char
   * alphanumeric codes (e.g. "M7", "8S", "AA"). Use {@link airlineIataCode}.
   */
  iataCode: z.string().length(3, "IATA code must be 3 characters").toUpperCase(),
  /**
   * ICAO country/airport code (4 uppercase letters)
   * @example "EPWA", "KJFK", "EGLL" (airport)
   * @warning Do NOT use this for AIRLINE ICAO codes — airlines use 3-char
   * codes (e.g. "MAA", "GTR", "AAL"). Use {@link airlineIcaoCode}.
   */
  icaoCode: z.string().length(4, "ICAO code must be 4 characters").toUpperCase(),
  /**
   * Airline IATA designator — 2 characters, can include digits.
   * Format: `[A-Z0-9]{2}` after uppercasing.
   * @example "M7", "8S", "AA", "LH"
   */
  airlineIataCode: z.string().toUpperCase().regex(
    /^[A-Z0-9]{2}$/,
    "Airline IATA code must be 2 characters (letters or digits)"
  ),
  /**
   * Airline ICAO designator — 3 letters.
   * Format: `[A-Z]{3}` after uppercasing.
   * @example "MAA", "GTR", "AAL", "DLH"
   */
  airlineIcaoCode: z.string().toUpperCase().regex(/^[A-Z]{3}$/, "Airline ICAO code must be 3 letters"),
  /**
   * AIRAC cycle number as integer
   * @example 27, 28
   */
  airacCycleNumber: z.number().int("AIRAC cycle must be an integer").min(10, "AIRAC cycle must be at least 10"),
  /**
   * AIRAC cycle as string input (1 or more digits), automatically converted to number
   * Use this in forms where users type the cycle as a string
   * @example "27" -> 27
   */
  airacCycleString: z.string().regex(/^\d+$/, "AIRAC cycle must contain only digits").transform((val) => parseInt(val, 10)),
  /**
   * Additional information or notes (optional text)
   */
  additionalInfo: z.string().optional(),
  /**
   * AFTN address - stores AFTN Safe (mixed case allowed)
   * Convert to AFTN Strict manually when displaying/transmitting
   *
   * @input_filter characterFilters.AFTN_SAFE
   * @storage AFTN Safe (keeps case)
   * @display Use toAftnStrict() to convert to uppercase
   *
   * @example
   * Input: "aftn-example"
   * Stored: "aftn-example"
   * Display: toAftnStrict("aftn-example") -> "AFTN-EXAMPLE"
   */
  aftnAddress: z.string().min(1, "AFTN address is required").regex(AFTN_SAFE_REGEX, "AFTN address contains invalid characters"),
  /**
   * AFTN-safe text field (allows lowercase, validates AFTN chars)
   * Use for: Remarks, notes, descriptions that may go to AFTN
   *
   * @input_filter characterFilters.AFTN_SAFE
   * @storage AFTN Safe (keeps case)
   * @display Use toAftnStrict() when generating AFTN messages
   */
  aftnSafeText: z.string().regex(AFTN_SAFE_REGEX, "Text contains characters not supported by AFTN"),
  /**
   * Required email address
   * @example "user@example.com"
   */
  requiredEmail: z.string().min(1, "Email is required").email("Must be a valid email address"),
  /**
   * Optional email address (allows empty string)
   * @example "user@example.com" or ""
   */
  optionalEmail: z.string().email("Must be a valid email address").optional().or(z.literal(""))
};

export {
  fieldSchemas
};
